import React, { useState, useEffect, useRef } from 'react';
import { Steps, Button, Card, Alert, Space, Spin, Typography, message } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import EntitySelector from './EntitySelector';
import EntityEditor from './EntityEditor';
import EntitlementTableEditor, { EntitlementTableEditorHandle } from './EntitlementTableEditor';
import RegionsQuestionsEditor from './RegionsQuestionsEditor';
import ReviewChanges from './ReviewChanges';
import { UpdateWizardStep, UpdateOperation, EntityType } from './types';
import { useUpdateWizard } from './hooks';
import { applyUpdates } from './utils';
import { apiService } from '../../services/api';
import './UpdateWizard.css';

const { Title } = Typography;

const DEFAULT_STEPS = ['Select Entity', 'Edit Details', 'Review & Submit'];
const PRODUCT_STEPS = ['Select Entity', 'Edit Details', 'Regions & Questions', 'Review & Submit'];
const ENTITLEMENT_STEPS = ['Select Entity', 'Edit Details', 'Edit Entitlements', 'Regions & Questions', 'Review & Submit'];

const UpdateWizard: React.FC = () => {
  const {
    loading,
    error,
    currentStep,
    selectedEntity,
    selectedEntityData,
    updates,
    relatedUpdates,
    setCurrentStep,
    setSelectedEntity,
    setSelectedEntityData,
    addUpdate,
    addRelatedUpdate: _addRelatedUpdate,
    clearError,
    clearUpdates,
    reset,
  } = useUpdateWizard();

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [alsoUpdateEntitlements, setAlsoUpdateEntitlements] = useState(false);
  const entitlementEditorRef = useRef<EntitlementTableEditorHandle>(null);

  // Determine if we should show the entitlements step
  const showEntitlementsStep = alsoUpdateEntitlements && selectedEntity === EntityType.PRODUCT;
  const isProductEntity = selectedEntity === EntityType.PRODUCT;

  // Step list: product entities always get Regions & Questions step
  const STEPS = showEntitlementsStep
    ? ENTITLEMENT_STEPS
    : isProductEntity
      ? PRODUCT_STEPS
      : DEFAULT_STEPS;

  // Index helpers
  const getReviewStep = () => STEPS.length - 1;
  const getRegionsQuestionsStep = () =>
    showEntitlementsStep
      ? UpdateWizardStep.EDIT_REGIONS_QUESTIONS  // = 3
      : UpdateWizardStep.EDIT_REGIONS_QUESTIONS - 1; // = 2 for PRODUCT_STEPS
  const getEntitlementsStep = () => UpdateWizardStep.EDIT_ENTITLEMENTS;

  // Automatically progress to edit step when entity data is selected
  useEffect(() => {
    if (currentStep === UpdateWizardStep.SELECT_ENTITY && selectedEntityData && selectedEntity) {
      // Go directly to edit step for all entities
      setCurrentStep(UpdateWizardStep.EDIT_DETAILS);
    }
  }, [selectedEntityData, selectedEntity, currentStep, setCurrentStep]);

  const handleNext = async () => {
    if (currentStep === UpdateWizardStep.SELECT_ENTITY && selectedEntity) {
      if (!selectedEntityData) {
        return;
      }
      setCurrentStep(UpdateWizardStep.EDIT_DETAILS);
    } else if (currentStep === UpdateWizardStep.EDIT_DETAILS) {
      if (showEntitlementsStep) {
        setCurrentStep(getEntitlementsStep());
      } else if (isProductEntity) {
        setCurrentStep(getRegionsQuestionsStep());
      } else {
        setCurrentStep(getReviewStep());
      }
    } else if (currentStep === getEntitlementsStep() && showEntitlementsStep) {
      // Validate all required fields before advancing
      const isValid = await entitlementEditorRef.current?.validate();
      if (!isValid) {
        message.error('Please fill in all required fields before continuing.');
        return;
      }
      setCurrentStep(getRegionsQuestionsStep());
    } else if (currentStep === getRegionsQuestionsStep() && isProductEntity) {
      setCurrentStep(getReviewStep());
    } else if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      if (currentStep === UpdateWizardStep.EDIT_DETAILS) {
        // Going back from EDIT_DETAILS to SELECT_ENTITY
        setSelectedEntityData(null);
        clearUpdates();
        setAlsoUpdateEntitlements(false);
        setCurrentStep(UpdateWizardStep.SELECT_ENTITY);
      } else if (currentStep === getEntitlementsStep() && showEntitlementsStep) {
        // Going back from EDIT_ENTITLEMENTS to EDIT_DETAILS — keep updates
        setCurrentStep(UpdateWizardStep.EDIT_DETAILS);
      } else if (currentStep === getRegionsQuestionsStep() && isProductEntity) {
        // Going back from REGIONS_QUESTIONS
        if (showEntitlementsStep) {
          setCurrentStep(getEntitlementsStep());
        } else {
          setCurrentStep(UpdateWizardStep.EDIT_DETAILS);
        }
      } else if (currentStep === getReviewStep()) {
        // Going back from REVIEW — keep all updates so forms can restore state
        if (isProductEntity) {
          setCurrentStep(getRegionsQuestionsStep());
        } else {
          setCurrentStep(UpdateWizardStep.EDIT_DETAILS);
        }
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const allUpdates: UpdateOperation[] = [...updates, ...relatedUpdates];
      // One request ID for every server call made in this submit so the
      // backend can correlate all downstream work under one audit trail.
      await apiService.runOperation(() => applyUpdates(allUpdates));
      setSubmitSuccess(true);

      setTimeout(() => {
        setSubmitSuccess(false);
        setAlsoUpdateEntitlements(false);
        reset();
      }, 2000);
    } catch (err) {
      message.error('Failed to apply updates: ' + (err as Error).message);
    }
  };

  const canProceed = (): boolean => {
    if (currentStep === UpdateWizardStep.SELECT_ENTITY) {
      return !!selectedEntity && !!selectedEntityData;
    }
    if (currentStep === UpdateWizardStep.EDIT_DETAILS) {
      // Allow proceeding if there are updates OR if alsoUpdateEntitlements is checked OR it is a product
      return updates.length > 0 || alsoUpdateEntitlements || isProductEntity;
    }
    if (showEntitlementsStep && currentStep === getEntitlementsStep()) {
      return true; // Entitlements step can always proceed
    }
    if (isProductEntity && currentStep === getRegionsQuestionsStep()) {
      return true; // Regions & Questions step can always proceed
    }
    if (currentStep === getReviewStep()) {
      return true;
    }
    return false;
  };

  const renderStepContent = () => {
    if (currentStep === UpdateWizardStep.SELECT_ENTITY) {
      return (
        <EntitySelector
          selectedEntity={selectedEntity}
          onEntitySelect={setSelectedEntity}
          onEntityDataSelect={setSelectedEntityData}
        />
      );
    }

    if (currentStep === UpdateWizardStep.EDIT_DETAILS) {
      return selectedEntityData && selectedEntity ? (
        <EntityEditor
          entityType={selectedEntity}
          entityData={selectedEntityData}
          onUpdate={addUpdate}
          alsoUpdateEntitlements={alsoUpdateEntitlements}
          onAlsoUpdateEntitlementsChange={setAlsoUpdateEntitlements}
          existingUpdates={updates}
        />
      ) : null;
    }

    if (showEntitlementsStep && currentStep === getEntitlementsStep()) {
      return selectedEntityData ? (
        <EntitlementTableEditor
          ref={entitlementEditorRef}
          productId={selectedEntityData.id as number}
          productName={(selectedEntityData.name as string) || ''}
          onUpdate={addUpdate}
          existingUpdates={updates}
        />
      ) : null;
    }

    if (isProductEntity && currentStep === getRegionsQuestionsStep()) {
      return selectedEntityData ? (
        <RegionsQuestionsEditor
          productId={selectedEntityData.id as number}
          productName={(selectedEntityData.name as string) || ''}
          onUpdate={addUpdate}
          existingUpdates={updates}
        />
      ) : null;
    }

    if (currentStep === getReviewStep()) {
      return selectedEntity ? (
        <ReviewChanges
          mainUpdates={updates}
          relatedUpdates={relatedUpdates}
          entityType={selectedEntity}
          entityData={selectedEntityData}
        />
      ) : null;
    }

    return null;
  };

  if (submitSuccess) {
    return (
      <div className="update-wizard">
        <div className="update-wizard-success">
          <Alert
            message="Success"
            description="Updates applied successfully!"
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="update-wizard">
      <Title level={2}>Update Wizard</Title>

      <Steps
        current={currentStep}
        items={STEPS.map((label) => ({ title: label }))}
      />

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={clearError}
          className="update-wizard-alert"
        />
      )}

      <Card>
        {loading ? (
          <div className="update-wizard-loading">
            <Spin size="large" />
          </div>
        ) : (
          renderStepContent()
        )}
      </Card>

      <div className="update-wizard-actions">
        <Button
          onClick={handleBack}
          disabled={currentStep === 0 || loading}
          icon={<ArrowLeftOutlined />}
        >
          Back
        </Button>

        <Space>
          <Button onClick={reset} disabled={loading}>
            Cancel
          </Button>

          {currentStep === STEPS.length - 1 ? (
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              icon={<SaveOutlined />}
            >
              Submit Changes
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={() => { void handleNext(); }}
              disabled={!canProceed() || loading}
              icon={<ArrowRightOutlined />}
            >
              Next
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};

export default UpdateWizard;
